import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Clock, Zap, Navigation, ArrowLeft, BellRing, CheckCircle2, MessageSquare, ShieldCheck, XCircle, PlayCircle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import GigReviewDialog from '../components/GigReviewDialog';
import { GIG_CATEGORIES, GIG_STATUS } from '../lib/gigMockData';
import { resolveAssetUrl } from '../lib/assets';
import MiniLocationMap from '../components/MiniLocationMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GigDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [gig, setGig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [applications, setApplications] = useState([]);
  const [applied, setApplied] = useState(false);

  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  const fetchGig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gigs/${id}`, { withCredentials: true });
      setGig(data);
      if (data.status === 'completed' && user) {
        try {
          const mr = await axios.get(`${API}/gigs/${id}/my-review`, { withCredentials: true });
          setHasReviewed(!!mr.data.exists);
        } catch (e) { /* ignore */ }
      }
      // Gig owner: load the applicant list.
      if (user && data.buyer_id === user._id) {
        try {
          const ap = await axios.get(`${API}/gigs/${id}/applications`, { withCredentials: true });
          setApplications(ap.data.applications || []);
        } catch (e) { /* ignore */ }
      }
    } catch (e) {
      setGig(null);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => { fetchGig(); }, [fetchGig]);

  const applyToGig = async () => {
    if (!user) { navigate('/login'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/gigs/${id}/apply`, {}, { withCredentials: true });
      setApplied(true);
      toast.success(tr('Pieteikums nosūtīts!', 'Application sent!', 'Заявка отправлена!'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : tr('Neizdevās', 'Failed', 'Ошибка'));
      if (err?.response?.status === 401) navigate('/login');
    } finally { setBusy(false); }
  };

  const ignoreGig = async () => {
    if (!user) { navigate('/login'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/gigs/${id}/ignore`, {}, { withCredentials: true });
      toast.success(tr('Ignorēts', 'Ignored', 'Скрыто'));
      navigate('/gigs');
    } catch (err) { /* ignore */ } finally { setBusy(false); }
  };

  const assignWorker = async (workerId, workerName) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/gigs/${id}/assign/${workerId}`, {}, { withCredentials: true });
      setGig(data);
      toast.success(tr(`Izvēlēts: ${workerName}`, `Selected: ${workerName}`, `Выбрано: ${workerName}`));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : tr('Neizdevās', 'Failed', 'Ошибка'));
    } finally { setBusy(false); }
  };

  const action = async (path, okMsg) => {
    if (!user) { navigate('/login'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/gigs/${id}/${path}`, {}, { withCredentials: true });
      setGig(data);
      toast.success(okMsg);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : tr('Darbība neizdevās', 'Action failed', 'Ошибка'));
      if (err?.response?.status === 401) navigate('/login');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D97736]" /></div>;
  }

  if (!gig) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#5C5C5C] mb-4">{tr('Darbs nav atrasts.', 'Gig not found.', 'Работа не найдена.')}</p>
          <Link to="/gigs"><Button variant="outline">{tr('Atpakaļ uz haltūrām', 'Back to gigs', 'Назад')}</Button></Link>
        </div>
      </div>
    );
  }

  const cat = GIG_CATEGORIES[gig.category];
  const catLabel = gig.category === 'other' && gig.category_other
    ? gig.category_other
    : (cat ? cat[language] || cat.lv : gig.category);
  const status = GIG_STATUS[gig.status] || GIG_STATUS.open;
  const isBuyer = user && gig.buyer_id === user._id;
  const isWorker = user && gig.worker_id === user._id;

  const formatSchedule = () => {
    if (gig.schedule === 'asap') return tr('Tagad / pēc iespējas ātrāk', 'ASAP', 'Сейчас / как можно скорее');
    if (!gig.scheduled_at) return '—';
    const d = new Date(gig.scheduled_at);
    return d.toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US', {
      weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="gig-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/gigs')} className="flex items-center gap-1 text-[#5C5C5C] hover:text-[#D97736] mb-6 text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back', 'Назад')}
        </button>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-[#E5E4E0] rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Badge className="bg-[#F3F2EE] text-[#5C5C5C] border-0">{catLabel}</Badge>
                {gig.schedule === 'asap' && (
                  <span className="inline-flex items-center gap-1 text-[#D97736] text-xs font-semibold">
                    <Zap className="w-3.5 h-3.5" /> {tr('Steidzami', 'Urgent', 'Срочно')}
                  </span>
                )}
                <Badge className={`${status.color} text-white border-0 ml-auto`} data-testid="gig-status-badge">{status[language] || status.lv}</Badge>
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold font-outfit text-[#121212] mb-4">{gig.title}</h1>
              {gig.description && <p className="text-[#5C5C5C] font-figtree leading-relaxed whitespace-pre-line">{gig.description}</p>}

              {gig.images?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4" data-testid="gig-images">
                  {gig.images.map((url, i) => (
                    <a key={i} href={resolveAssetUrl(url)} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-[#F3F2EE]" data-testid={`gig-detail-image-${i}`}>
                      <img src={resolveAssetUrl(url)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </a>
                  ))}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#F3F2EE]">
                <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                  <MapPin className="w-4 h-4 text-[#D97736]" />
                  <span>{gig.location_name || gig.city || gig.region}</span>
                </div>
                {gig.address && (
                  <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                    <MapPin className="w-4 h-4 text-[#5C5C5C]" />
                    <span>{gig.address}</span>
                  </div>
                )}
                {gig.distance_km != null && (
                  <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                    <Navigation className="w-4 h-4 text-[#4A6B53]" />
                    <span>{gig.distance_km} km {tr('no tevis', 'from you', 'от вас')}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                  <Clock className="w-4 h-4 text-[#6366f1]" />
                  <span>{formatSchedule()}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                  <BellRing className="w-4 h-4 text-[#D97736]" />
                  <span>{tr('Paziņots', 'Notified', 'Уведомлено')}: {gig.radius_km} km {tr('rādiusā', 'radius', 'радиус')}</span>
                </div>
              </div>

              {gig.lat != null && gig.lng != null && (
                <div className="mt-6 pt-6 border-t border-[#F3F2EE]">
                  <MiniLocationMap lat={gig.lat} lng={gig.lng} height={200}
                    label={tr('Darba atrašanās vieta', 'Job location', 'Местоположение работы')} />
                </div>
              )}
            </div>

            <div className="bg-white border border-[#E5E4E0] rounded-xl p-6">
              <h2 className="font-outfit font-semibold text-[#121212] mb-4">{tr('Darba devējs', 'Posted by', 'Заказчик')}</h2>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#D97736] flex items-center justify-center text-white text-lg font-medium">
                  {(gig.buyer_name || '?').charAt(0)}
                </div>
                <Link to={`/pro/${gig.buyer_id}`} className="font-medium text-[#121212] hover:text-[#D97736]" data-testid="buyer-pro-link">{gig.buyer_name}</Link>
              </div>
              {gig.worker_name && (
                <div className="mt-4 pt-4 border-t border-[#F3F2EE]">
                  <p className="text-sm text-[#5C5C5C] mb-2">{tr('Izpildītājs', 'Worker', 'Исполнитель')}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#4A6B53] flex items-center justify-center text-white text-sm font-medium">
                      {gig.worker_name.charAt(0)}
                    </div>
                    <Link to={`/pro/${gig.worker_id}`} className="font-medium text-[#121212] hover:text-[#D97736]" data-testid="worker-pro-link">{gig.worker_name}</Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action card */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E5E4E0] rounded-xl p-6 lg:sticky lg:top-20" data-testid="gig-action-card">
              <div className="text-3xl font-bold font-outfit text-[#121212] mb-1">€{gig.budget}</div>
              <p className="text-sm text-[#5C5C5C] mb-6">{tr('Atlīdzība par darbu', 'Reward for this gig', 'Оплата за работу')}</p>

              {/* Worker: apply to an open gig (owner picks from applicants) */}
              {gig.status === 'open' && !isBuyer && (
                applied ? (
                  <div className="w-full rounded-lg p-3 mb-3 text-sm text-center font-medium bg-green-50 text-[#4A6B53]" data-testid="applied-msg">
                    {tr('Pieteikums nosūtīts — gaidi autora izvēli', 'Application sent — awaiting selection', 'Заявка отправлена')}
                  </div>
                ) : (
                  <div className="space-y-2 mb-3">
                    <Button onClick={applyToGig} disabled={busy}
                      className="w-full bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="apply-gig-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> {tr('Pieteikties', 'Apply', 'Откликнуться')}
                    </Button>
                    <Button onClick={ignoreGig} disabled={busy} variant="outline"
                      className="w-full border-[#E5E4E0] text-[#5C5C5C]" data-testid="ignore-gig-btn">
                      {tr('Ignorēt', 'Ignore', 'Скрыть')}
                    </Button>
                  </div>
                )
              )}

              {/* Owner: applicant list with select buttons */}
              {isBuyer && gig.status === 'open' && (
                <div className="mb-3 border border-[#E5E4E0] rounded-xl p-4" data-testid="applicants-list">
                  <p className="font-medium text-[#121212] mb-3 text-sm">
                    {tr('Pretendenti', 'Applicants', 'Кандидаты')} ({applications.length})
                  </p>
                  {applications.length === 0 ? (
                    <p className="text-sm text-[#9CA3AF]">{tr('Vēl nav pretendentu', 'No applicants yet', 'Пока нет кандидатов')}</p>
                  ) : (
                    <div className="space-y-3">
                      {applications.map((a) => (
                        <div key={a.worker_id} className="flex items-center gap-3" data-testid={`applicant-${a.worker_id}`}>
                          <div className="w-9 h-9 rounded-full bg-[#4A6B53] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                            {a.worker_name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <Link to={`/pro/${a.worker_id}`} className="font-medium text-[#121212] hover:text-[#D97736] text-sm">{a.worker_name}</Link>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {a.reputation && <span className="text-xs text-[#5C5C5C]">★ {(a.reputation.avg_rating || 0).toFixed(1)} · {a.reputation.review_count || 0}</span>}
                              {(a.tags || []).slice(0, 3).map((t) => (
                                <span key={t} className="text-xs text-[#D97736]">#{t}</span>
                              ))}
                            </div>
                          </div>
                          <Button size="sm" onClick={() => assignWorker(a.worker_id, a.worker_name)} disabled={busy}
                            className="bg-[#D97736] hover:bg-[#C06528] text-white shrink-0" data-testid={`assign-${a.worker_id}`}>
                            {tr('Izvēlēties', 'Select', 'Выбрать')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Worker: start an accepted gig */}
              {isWorker && gig.status === 'accepted' && (
                <Button onClick={() => action('start', tr('Darbs uzsākts', 'Gig started', 'Начато'))} disabled={busy}
                  className="w-full bg-[#6366f1] hover:bg-[#4f46e5] text-white mb-3" data-testid="start-gig-btn">
                  <PlayCircle className="w-4 h-4 mr-1" /> {tr('Uzsākt darbu', 'Start gig', 'Начать')}
                </Button>
              )}

              {/* Buyer: confirm completion → release escrow */}
              {isBuyer && ['accepted', 'in_progress'].includes(gig.status) && (
                <Button onClick={() => action('complete', tr('Darbs apstiprināts — samaksa veikta', 'Completed — payment released', 'Завершено'))} disabled={busy}
                  className="w-full bg-[#4A6B53] hover:bg-[#3d5944] text-white mb-3" data-testid="complete-gig-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> {tr('Apstiprināt pabeigšanu', 'Confirm completion', 'Подтвердить')}
                </Button>
              )}

              {/* Cancel: buyer (open/accepted/in_progress) or worker (decline) */}
              {((isBuyer && ['open', 'accepted', 'in_progress'].includes(gig.status)) ||
                (isWorker && ['accepted', 'in_progress'].includes(gig.status))) && (
                <Button onClick={() => action('cancel', isWorker && !isBuyer
                    ? tr('Tu atteicies no darba', 'You declined the gig', 'Вы отказались')
                    : tr('Darbs atcelts — depozīts atmaksāts', 'Gig cancelled — escrow refunded', 'Отменено'))}
                  disabled={busy} variant="outline"
                  className="w-full border-red-200 text-red-600 hover:bg-red-50 mb-3" data-testid="cancel-gig-btn">
                  <XCircle className="w-4 h-4 mr-1" />
                  {isWorker && !isBuyer ? tr('Atteikties no darba', 'Decline gig', 'Отказаться') : tr('Atcelt darbu', 'Cancel gig', 'Отменить')}
                </Button>
              )}

              {['completed', 'cancelled'].includes(gig.status) && (
                <div className={`rounded-lg p-3 mb-3 text-sm text-center font-medium ${gig.status === 'completed' ? 'bg-green-50 text-[#4A6B53]' : 'bg-[#F3F2EE] text-[#5C5C5C]'}`} data-testid="closed-msg">
                  {gig.status === 'completed' ? tr('Darbs pabeigts', 'Gig completed', 'Работа завершена') : tr('Darbs atcelts', 'Gig cancelled', 'Работа отменена')}
                </div>
              )}

              {/* Leave a review after completion */}
              {gig.status === 'completed' && (isBuyer || isWorker) && !hasReviewed && (
                <Button onClick={() => setReviewOpen(true)}
                  className="w-full bg-[#D97736] hover:bg-[#C06528] text-white mb-3" data-testid="leave-review-btn">
                  <Star className="w-4 h-4 mr-1" /> {tr('Atstāt atsauksmi', 'Leave a review', 'Оставить отзыв')}
                </Button>
              )}
              {gig.status === 'completed' && hasReviewed && (
                <div className="flex items-center gap-2 bg-[#F3F2EE] text-[#5C5C5C] rounded-lg p-3 mb-3 text-sm" data-testid="reviewed-msg">
                  <Star className="w-4 h-4 fill-[#D97736] text-[#D97736]" /> {tr('Tu jau novērtēji šo darbu', 'You already reviewed this gig', 'Вы уже оценили')}
                </div>
              )}

              <Button variant="outline" className="w-full border-[#E5E4E0] text-[#5C5C5C]" data-testid="message-poster-btn">
                <MessageSquare className="w-4 h-4 mr-1" /> {tr('Rakstīt ziņu', 'Send message', 'Написать')}
              </Button>

              <p className="text-xs text-[#5C5C5C] mt-4 flex items-start gap-1">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#4A6B53]" />
                {tr('Maksājums tiek aizturēts droši (escrow) līdz darba pabeigšanai.', 'Payment is held securely in escrow until the gig is completed.', 'Оплата удерживается до завершения работы.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <GigReviewDialog
        gigId={id}
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        onReviewed={() => { setHasReviewed(true); fetchGig(); }}
      />
    </div>
  );
}
